/**
 * When to tell a student mid-dialogue that we cannot hear them.
 *
 * Digital silence only: a working microphone in a quiet room reads about −70
 * dBFS rms, so a near-silence trigger would fire on healthy hardware. A
 * microphone that is low but not zero gets no nudge — the volume meter and the
 * telemetry cover that case instead.
 */
import { type LevelSample, samplesForMs } from "@/lib/audio-level";

/** Bounded by the 10 s minimum preparation, leaving 7 s to act in. */
const NUDGE_SHOW_MS = 3_000;

/** Sound that takes the nudge back down. */
const NUDGE_CLEAR_MS = 2_000;

export const NUDGE_SHOW_SAMPLES = samplesForMs(NUDGE_SHOW_MS);

export const NUDGE_CLEAR_SAMPLES = samplesForMs(NUDGE_CLEAR_MS);

/** Readings can stop for good, and only sound otherwise clears the nudge. */
export const NUDGE_STALE_MS = NUDGE_CLEAR_MS;

export interface SilentAudioNudgeTracker {
  /** @returns Whether the nudge should be up after this sample. */
  add(level: LevelSample, contextState: AudioContextState): boolean;
}

/**
 * Reads the per-sample silence flag at 250 ms rather than the 10 s window
 * aggregate, which cannot react inside a 10 s preparation phase.
 *
 * A context that is not running hands out zeros of its own, so those samples are
 * dropped rather than counted. Dropping holds the run under way instead of
 * restarting it.
 */
export function createSilentAudioNudgeTracker(): SilentAudioNudgeTracker {
  let silentSamples = 0;
  let soundSamples = 0;
  let shown = false;

  return {
    add(level, contextState) {
      if (contextState !== "running") return shown;

      if (level.digitallySilent) {
        silentSamples += 1;
        soundSamples = 0;
        if (silentSamples >= NUDGE_SHOW_SAMPLES) shown = true;
      } else {
        soundSamples += 1;
        silentSamples = 0;
        if (soundSamples >= NUDGE_CLEAR_SAMPLES) shown = false;
      }

      return shown;
    },
  };
}
